import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { NativeClientService } from '../../shared/services/native-client.service';
import { MatDialog } from '@angular/material/dialog';
import { defaultDialogConfig, DialogData, XtDialogComponent } from '@xt/ui-kit';
import { select, Store } from '@ngrx/store';
import { userRoleNewSelector } from '@core/store/selectors/user-role.selectors';
import { verifiedUserRoles } from '@core/store/reducers/session-info.reducer';

/**
 * Guard to prevent verified users from accessing certain routes on mobile devices.
 *
 * This guard implements the following logic:
 * 1. If the user is NOT on a mobile device → Allow access
 * 2. If the user IS on a mobile device:
 *    - If user has NO verified roles (unverified/onboarding user) → Allow access
 *      (This enables PIN link onboarding flow on mobile for patient)
 *    - If user HAS verified roles → Block access and show informational dialog
 *      - If this is an initial page load (direct navigation) → Redirect to dashboard after dialog closes
 *      - If user navigated from within the app → Stay on previous page (navigation is cancelled)
 *
 * @param route - The activated route snapshot, may contain custom dialog text in route.data.preventMobileGuardText
 * @returns Observable<boolean> - true to allow navigation, false to block it
 */
export function isMobileDeviceGuard(
  preventMobileGuardText?: string[],
): CanActivateFn {
  return (): Observable<boolean> => {
    const nativeClientService = inject(NativeClientService);
    const router = inject(Router);
    const dialog = inject(MatDialog);
    const store = inject(Store);

    const initialLoad = !router.currentNavigation()?.previousNavigation;

    if (nativeClientService.isMobileDevice) {
      return store.pipe(
        select(userRoleNewSelector),
        take(1),
        switchMap((roles) => {
          const hasVerifiedRole =
            roles?.length &&
            roles.some((item) => verifiedUserRoles.includes(item));
          // Allow access if user has unverified roles
          // to make onboarding with pin link possible
          if (!hasVerifiedRole) return of(true);
          // Block access and show dialog if user has verified roles
          return showMobileDeviceRestrictionDialog(
            dialog,
            preventMobileGuardText,
          ).pipe(
            tap(() => initialLoad && router.navigate(['/dashboard'])),
            map(() => false),
          );
        }),
      );
    }

    return of(true);
  };
}

function showMobileDeviceRestrictionDialog(
  dialog: MatDialog,
  preventMobileGuardText?: string[],
): Observable<boolean> {
  const dialogRef = dialog.open<XtDialogComponent, DialogData>(
    XtDialogComponent,
    {
      data: {
        titleIcon: 'info',
        title: 'app.labels.hint',
        cancelButtonLabel: 'app.wizard.ctas.close',
        paragraphs_HTML: preventMobileGuardText ?? [
          'app.identification.commonMobileGuardMessage',
        ],
        dataCy: 'portal.mobile-device-restriction-dialog',
      },
      panelClass: 'dialog-bottom-sheet',
      width: '100%',
      maxWidth: '480px',
      position: { bottom: '0' },
      ...defaultDialogConfig,
    },
  );

  return dialogRef.afterClosed().pipe(map(() => false));
}
